import {
    Box,
    Button,
    Paper,
    Typography,
    Chip,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import toast, { Toaster } from "react-hot-toast";
import * as services from "../../services/services";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import { formatDate } from "../../utils/formatDate";
import { width } from "@mui/system";

const ConnectionRequestList = () => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await services.getPetLinksByStatus();
            const rawData = res.data.data || [];

            const formattedRows = rawData.map((item, index) => ({
                id: item.id,
                no: index + 1,
                petId: item.pet?.id || "-",
                hospitalPetCode: item.hospitalPet?.hospitalPetCode || item.hospitalPetId || "-",
                ownerPetId: item.ownerPetId || "-",
                status: item.status,
                requestedBy: item.requestedBy || "-",
                createdAt: formatDate(item.createdAt) || "-",
            }));

            setRows(formattedRows);
        } catch (error) {
            console.error("Failed to fetch requests", error);
            toast.error("ไม่สามารถโหลดข้อมูลคำขอได้");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleAction = async (id, newStatus) => {
        try {
            // Optimistic update or refresh
            await services.updatePetLinkStatus(id, newStatus);
            toast.success(newStatus === "LINKED" ? "ยอมรับคำขอเรียบร้อย" : "ปฏิเสธคำขอเรียบร้อย");
            fetchRequests(); // Refresh list to remove the item
        } catch (error) {
            console.error("Failed to update status", error);
            toast.error("ทำรายการไม่สำเร็จ");
        }
    };

    const columns = [
        { field: "no", headerName: "No", flex: 0.5 },
        { field: "createdAt", headerName: "วันที่ขอเชื่อมต่อ", flex: 1.5 },
        { field: "hospitalPetCode", headerName: "IDสัตว์เลี้ยง", flex: 1 },
        { field: "ownerPetId", headerName: "IDเจ้าของสัตว์เลี้ยง", flex: 1 },
        { field: "requestedBy", headerName: "ผู้ขอเชื่อมต่อ", flex: 1 },

        {
            field: "status",
            headerName: "สถานะ",
            flex: 1,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color={params.value === "PENDING" ? "warning" : params.value === "LINKED" ? "success" : "default"}
                    size="small"
                />
            ),
        },
        {
            field: "actions",
            headerName: "จัดการ",
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => navigate(`/pets/connection-requests/${params.row.id}`, { state: { request: params.row } })}
                    >
                        จัดการ
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <>
            <AppBreadcrumbs />
            <Toaster position="bottom-left" reverseOrder={false} />
            <Box sx={{ my: 2 }}>
                <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
                    รายการขอเชื่อมต่อสัตว์เลี้ยง
                </Typography>
                <Paper sx={{ height: 600, width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        pageSize={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        disableSelectionOnClick
                    />
                </Paper>
            </Box>
        </>
    );
};

export default ConnectionRequestList;
